import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './CentralAdvertencia.css';
import {
    FaExclamationTriangle, FaPlus, FaFilter, FaSpinner,
    FaCalendarAlt, FaTrash, FaRegCommentDots, FaRegFileAlt,
    FaCheck, FaTimes, FaPrint, FaUpload, FaEye, FaInfoCircle, 
    FaFileSignature, FaClock
} from 'react-icons/fa';

// --- Constantes ---
const LISTA_MOTIVOS_PADRAO = [
    "Falta injustificada",
    "Atraso frequente",
    "Não cumprimento de normas de segurança (EPI)",
    "Insubordinação ou desrespeito",
    "Uso indevido de equipamentos",
    "Violação do código de conduta",
    "Baixo desempenho ou negligência",
    "Outro (descrever no registro interno)"
];

// --- Componentes Visuais ---
const StatusBadge = ({ status }) => {
    const map = {
        'pendente': { class: 'badge-warning', label: 'Pendente' },
        'aprovada': { class: 'badge-success', label: 'Aprovada' },
        'rejeitada': { class: 'badge-danger', label: 'Rejeitada' },
        'concluida': { class: 'badge-info', label: 'Concluída' }
    };
    const info = map[status] || { class: 'badge-gray', label: status };
    return <span className={`status-badge ${info.class}`}>{info.label}</span>;
};

const SeverityIcon = ({ tipo }) => {
    const map = {
        'leve': { icon: <FaRegCommentDots />, class: 'icon-yellow' },
        'media': { icon: <FaRegFileAlt />, class: 'icon-orange' },
        'grave': { icon: <FaExclamationTriangle />, class: 'icon-red' }
    };
    const info = map[tipo] || { icon: <FaInfoCircle />, class: 'icon-blue' };
    return <div className={`severity-icon ${info.class}`}>{info.icon}</div>;
};

// --- MODAIS ---

// 1. Modal de Pré-visualização de Impressão
const PrintPreviewModal = ({ isOpen, onClose, htmlContent }) => {
    const iframeRef = useRef(null);

    useEffect(() => {
        if (isOpen && iframeRef.current && htmlContent) {
            const doc = iframeRef.current.contentWindow.document;
            doc.open();
            doc.write(htmlContent);
            doc.close();
        }
    }, [isOpen, htmlContent]);

    const handlePrint = () => {
        if (iframeRef.current) {
            iframeRef.current.contentWindow.focus();
            iframeRef.current.contentWindow.print();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="overlay-backdrop">
            <div className="modal-premium modal-xl">
                <div className="modal-header">
                    <h3><FaPrint className="text-blue" /> Visualizar Documento</h3>
                    <button onClick={onClose} className="btn-close">×</button>
                </div>
                <div className="modal-body print-body">
                    <div className="iframe-container">
                        <iframe ref={iframeRef} title="Documento de Advertência" className="print-iframe" />
                    </div>
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn-ghost">Fechar</button>
                    <button onClick={handlePrint} className="btn-primary">
                        <FaPrint /> Imprimir Agora
                    </button>
                </div>
            </div>
        </div>
    );
};

// 2. Modal de Rejeição
const RejectModal = ({ isOpen, onClose, onSubmit }) => {
    const [justificativa, setJustificativa] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if(isOpen) setJustificativa(''); }, [isOpen]);

    const handleSubmit = async () => {
        if (!justificativa) return toast.warn('Justificativa é obrigatória.');
        setSubmitting(true);
        try { await onSubmit(justificativa); onClose(); } finally { setSubmitting(false); }
    };

    if (!isOpen) return null;
    return (
        <div className="overlay-backdrop">
            <div className="modal-premium">
                <div className="modal-header">
                    <h3><FaTimes className="text-red" /> Rejeitar Advertência</h3>
                    <button onClick={onClose} className="btn-close">×</button>
                </div>
                <div className="modal-body">
                    <label className="input-label">Motivo da Rejeição</label>
                    <textarea 
                        rows="4" className="input-modern"
                        value={justificativa} onChange={(e) => setJustificativa(e.target.value)}
                        placeholder="Explique o motivo..."
                    />
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn-ghost">Cancelar</button>
                    <button onClick={handleSubmit} className="btn-danger" disabled={submitting}>
                        {submitting ? 'Processando...' : 'Confirmar Rejeição'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// 3. Modal de Anexo
const AttachModal = ({ isOpen, onClose, onSubmit }) => {
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if(isOpen) setFile(null); }, [isOpen]);

    const handleSubmit = async () => {
        if (!file) return toast.warn('Selecione um arquivo.');
        setSubmitting(true);
        try { await onSubmit(file); onClose(); } finally { setSubmitting(false); }
    };

    if (!isOpen) return null;
    return (
        <div className="overlay-backdrop">
            <div className="modal-premium">
                <div className="modal-header">
                    <h3><FaUpload className="text-blue" /> Anexar Documento Assinado</h3>
                    <button onClick={onClose} className="btn-close">×</button>
                </div>
                <div className="modal-body">
                    <div className="upload-area">
                        <FaFileSignature size={32} />
                        <p>Selecione o arquivo digitalizado (PDF ou Imagem)</p>
                        <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files[0])} />
                    </div>
                    {file && <p className="file-selected">Arquivo: {file.name}</p>}
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn-ghost">Cancelar</button>
                    <button onClick={handleSubmit} className="btn-primary" disabled={submitting}>
                        {submitting ? 'Enviando...' : 'Salvar e Concluir'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// 4. Modal de Adicionar
const AddWarningModal = ({ isOpen, onClose, funcionarios, onSubmit, user, clientes }) => {
    const [formData, setFormData] = useState({
        target_cliente_id: user?.perfil === 'admin_geral' ? '' : user?.cliente_id,
        user_id: '', tipo: 'leve', data_ocorrencia: new Date().toISOString().slice(0, 10),
        motivo_padrao: LISTA_MOTIVOS_PADRAO[0], motivo_registro_interno: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const funcionariosFiltrados = useMemo(() => {
        if (user?.perfil !== 'admin_geral') {
            return Array.isArray(funcionarios) ? funcionarios.filter(f => String(f.cliente_id) === String(user.cliente_id)) : [];
        }
        if (!formData.target_cliente_id) return [];
        return Array.isArray(funcionarios) ? funcionarios.filter(f => String(f.cliente_id) === String(formData.target_cliente_id)) : [];
    }, [user, formData.target_cliente_id, funcionarios]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'target_cliente_id' && user?.perfil === 'admin_geral') {
            setFormData(prev => ({ ...prev, [name]: value, user_id: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.user_id || !formData.motivo_padrao) return toast.warn("Preencha os campos obrigatórios.");
        setSubmitting(true);
        try { await onSubmit(formData); onClose(); } finally { setSubmitting(false); }
    };

    if (!isOpen) return null;

    return (
        <div className="overlay-backdrop">
            <div className="modal-premium modal-large">
                <div className="modal-header">
                    <h3><FaExclamationTriangle className="text-orange" /> Registrar Advertência</h3>
                    <button onClick={onClose} className="btn-close">×</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body grid-form">
                    {user?.perfil === 'admin_geral' && (
                        <div className="form-group full">
                            <label className="input-label">Empresa Cliente</label>
                            <select className="input-modern" name="target_cliente_id" value={formData.target_cliente_id} onChange={handleChange} required>
                                <option value="">Selecione...</option>
                                {clientes?.map(c => <option key={c.id} value={c.id}>{c.nome_empresa}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="form-group">
                        <label className="input-label">Colaborador</label>
                        <select className="input-modern" name="user_id" value={formData.user_id} onChange={handleChange} required disabled={funcionariosFiltrados.length === 0}>
                            <option value="">Selecione...</option>
                            {funcionariosFiltrados.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="input-label">Data Ocorrência</label>
                        <input type="date" className="input-modern" name="data_ocorrencia" value={formData.data_ocorrencia} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                        <label className="input-label">Gravidade</label>
                        <select className="input-modern" name="tipo" value={formData.tipo} onChange={handleChange}>
                            <option value="leve">Leve (Verbal/Escrita Simples)</option>
                            <option value="media">Média (Suspensão 1-3 dias)</option>
                            <option value="grave">Grave (Suspensão/Justa Causa)</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="input-label">Motivo (Jurídico)</label>
                        <select className="input-modern" name="motivo_padrao" value={formData.motivo_padrao} onChange={handleChange}>
                            {LISTA_MOTIVOS_PADRAO.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div className="form-group full">
                        <label className="input-label">Registro Interno (Detalhes)</label>
                        <textarea className="input-modern" rows="3" name="motivo_registro_interno" value={formData.motivo_registro_interno} onChange={handleChange} placeholder="Contexto, testemunhas, detalhes..." />
                    </div>
                    <div className="modal-footer full">
                        <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
                        <button type="submit" className="btn-primary" disabled={submitting}>
                            {submitting ? 'Registrando...' : 'Registrar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente Principal ---
export default function CentralAdvertencia({ user }) {
    const [advertencias, setAdvertencias] = useState([]);
    const [funcionarios, setFuncionarios] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [selectedFuncionarioId, setSelectedFuncionarioId] = useState('todos');
    const [loading, setLoading] = useState(true);

    const [modalState, setModalState] = useState({ add: false, reject: false, attach: false, print: false });
    const [selectedAdvertencia, setSelectedAdvertencia] = useState(null);
    const [printHtml, setPrintHtml] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const reqs = [api.get('/api/rh/colaboradores'), api.get('/api/rh/advertencias')];
            if (user?.perfil === 'admin_geral') reqs.push(api.get('/api/clients/list'));
            
            const [funcRes, advRes, cliRes] = await Promise.all(reqs);
            setFuncionarios(funcRes.data.filter(f => f.status === 'ativo') || []);
            setAdvertencias(advRes.data || []);
            if (cliRes) setClientes(cliRes.data || []);
        } catch (error) {
            toast.error("Erro ao sincronizar dados.");
        } finally { setLoading(false); }
    };

    useEffect(() => { if (user?.id) fetchData(); }, [user]);

    const filteredList = useMemo(() => {
        let list = advertencias;
        if (selectedFuncionarioId !== 'todos') list = list.filter(a => String(a.user_id) === String(selectedFuncionarioId));
        return list.sort((a, b) => new Date(b.data_ocorrencia) - new Date(a.data_ocorrencia));
    }, [advertencias, selectedFuncionarioId]);

    const kpis = useMemo(() => {
        const counts = advertencias.reduce((acc, curr) => {
            acc[curr.status] = (acc[curr.status] || 0) + 1;
            return acc;
        }, { pendente: 0, aprovada: 0, concluida: 0 });
        return counts;
    }, [advertencias]);

    // --- Handlers ---

    const handleAdd = async (data) => {
        const res = await api.post('/api/rh/advertencias', data);
        toast.success("Advertência registrada.");
        setAdvertencias(prev => [res.data.advertencia || res.data, ...prev]);
    };

    const handleApprove = async (id) => {
        if (!window.confirm("Aprovar esta advertência?")) return;
        try {
            await api.post(`/api/rh/advertencias/${id}/aprovar`);
            toast.success("Aprovada com sucesso.");
            setAdvertencias(prev => prev.map(a => a.id === id ? { ...a, status: 'aprovada', aprovado_por_id: user.id } : a));
        } catch(e) { toast.error("Erro ao aprovar."); }
    };

    const handleReject = async (justificativa) => {
        if (!selectedAdvertencia) return;
        await api.post(`/api/rh/advertencias/${selectedAdvertencia.id}/rejeitar`, { justificativa });
        toast.info("Advertência rejeitada.");
        setAdvertencias(prev => prev.map(a => a.id === selectedAdvertencia.id ? { ...a, status: 'rejeitada', justificativa_rejeicao: justificativa } : a));
    };

    const handleAttach = async (file) => {
        const formData = new FormData();
        formData.append('documento_assinado', file);
        const res = await api.post(`/api/rh/advertencias/${selectedAdvertencia.id}/anexar-documento`, formData);
        toast.success("Documento anexado.");
        setAdvertencias(prev => prev.map(a => a.id === selectedAdvertencia.id ? { ...a, status: 'concluida', path_documento_assinado: res.data.path } : a));
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Excluir permanentemente?")) return;
        try {
            await api.delete(`/api/rh/advertencias/${id}`);
            toast.success("Excluído.");
            setAdvertencias(prev => prev.filter(a => a.id !== id));
        } catch(e) { toast.error("Erro ao excluir."); }
    };

    // 🔥 CORREÇÃO DEFINITIVA: Enviando o token como parâmetro 🔥
    const handlePreparePrint = async (id) => {
        const toastId = toast.loading("Gerando documento...");
        try {
            // Busca o token do localstorage (ajuste a chave se o seu sistema usa outra, ex: '@App:token')
            const token = localStorage.getItem('token'); 

            // Chama a API passando o token explicitamente na URL (params)
            // O middleware do backend (authQueryTokenMiddleware) exige isso.
            const response = await api.get(`/api/rh/advertencias/${id}/gerar-documento`, {
                params: { token: token },
                responseType: 'text'
            });

            // Salva o HTML e abre o modal
            setPrintHtml(response.data);
            setModalState({ ...modalState, print: true });

        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 401) {
                toast.error("Sessão expirada ou token inválido. Faça login novamente.");
            } else {
                toast.error("Erro ao gerar documento.");
            }
        } finally {
            toast.dismiss(toastId);
        }
    };

    if (loading) return <div className="loading-screen"><FaSpinner className="icon-spin" /></div>;

    return (
        <div className="central-adv-container fade-in">
            <div className="page-header">
                <div className="header-content">
                    <h1><FaExclamationTriangle /> Central de Advertências</h1>
                    <p>Gestão disciplinar e conformidade trabalhista.</p>
                </div>
                {(['admin_geral', 'admin', 'rh'].includes(user.perfil)) && (
                    <button className="btn-create" onClick={() => setModalState({ ...modalState, add: true })}>
                        <FaPlus /> Nova Ocorrência
                    </button>
                )}
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="icon-box yellow"><FaClock /></div>
                    <div className="stat-info"><h3>{kpis.pendente}</h3><span>Pendentes</span></div>
                </div>
                <div className="stat-card">
                    <div className="icon-box green"><FaCheck /></div>
                    <div className="stat-info"><h3>{kpis.aprovada}</h3><span>Aprovadas</span></div>
                </div>
                <div className="stat-card">
                    <div className="icon-box blue"><FaFileSignature /></div>
                    <div className="stat-info"><h3>{kpis.concluida}</h3><span>Concluídas</span></div>
                </div>
            </div>

            <div className="main-content-card">
                <div className="filter-bar">
                    <div className="search-wrapper">
                        <FaFilter />
                        <select value={selectedFuncionarioId} onChange={e => setSelectedFuncionarioId(e.target.value)}>
                            <option value="todos">Todos os Colaboradores</option>
                            {funcionarios.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                    </div>
                </div>

                <div className="timeline-feed">
                    {filteredList.length === 0 ? (
                        <div className="empty-state">Nenhum registro encontrado.</div>
                    ) : (
                        filteredList.map(adv => (
                            <div key={adv.id} className="timeline-card fade-in-up">
                                <div className="timeline-left">
                                    <SeverityIcon tipo={adv.tipo} />
                                    <div className="connector-line"></div>
                                </div>
                                <div className="timeline-body">
                                    <div className="card-header">
                                        <div className="user-meta">
                                            <strong>{adv.nome_usuario || 'Colaborador'}</strong>
                                            <span className="meta-date"><FaCalendarAlt /> {new Date(adv.data_ocorrencia).toLocaleDateString()}</span>
                                        </div>
                                        <StatusBadge status={adv.status} />
                                    </div>
                                    <div className="card-content">
                                        <h4 className="reason-title">{adv.motivo_padrao}</h4>
                                        {adv.motivo_registro_interno && <p className="internal-note"><FaInfoCircle /> <strong>Obs:</strong> {adv.motivo_registro_interno}</p>}
                                        {adv.status === 'rejeitada' && <div className="rejection-box"><strong>Motivo da Rejeição:</strong> {adv.justificativa_rejeicao}</div>}
                                    </div>
                                    <div className="card-actions">
                                        {user.perfil === 'admin_geral' && adv.status === 'pendente' && (
                                            <>
                                                <button className="btn-action approve" onClick={() => handleApprove(adv.id)} title="Aprovar"><FaCheck /></button>
                                                <button className="btn-action reject" onClick={() => { setSelectedAdvertencia(adv); setModalState({...modalState, reject: true}); }} title="Rejeitar"><FaTimes /></button>
                                            </>
                                        )}
                                        {adv.status === 'aprovada' && (
                                            <div className="action-group">
                                                <button className="btn-pill outline" onClick={() => handlePreparePrint(adv.id)}><FaPrint /> Imprimir</button>
                                                <button className="btn-pill primary" onClick={() => { setSelectedAdvertencia(adv); setModalState({...modalState, attach: true}); }}><FaUpload /> Anexar Assinado</button>
                                            </div>
                                        )}
                                        {adv.status === 'concluida' && adv.path_documento_assinado && (
                                            <a href={`${api.defaults.baseURL?.replace(/\/api$/, '')}${adv.path_documento_assinado.startsWith('/')?'':'/'}${adv.path_documento_assinado}`} target="_blank" rel="noopener noreferrer" className="btn-pill ghost"><FaEye /> Ver Documento</a>
                                        )}
                                        {user.perfil === 'admin_geral' && (
                                            <button className="btn-action delete" onClick={() => handleDelete(adv.id)} title="Excluir"><FaTrash /></button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <AddWarningModal isOpen={modalState.add} onClose={() => setModalState({...modalState, add: false})} funcionarios={funcionarios} onSubmit={handleAdd} user={user} clientes={clientes} />
            <RejectModal isOpen={modalState.reject} onClose={() => setModalState({...modalState, reject: false})} onSubmit={handleReject} />
            <AttachModal isOpen={modalState.attach} onClose={() => setModalState({...modalState, attach: false})} onSubmit={handleAttach} />
            <PrintPreviewModal isOpen={modalState.print} onClose={() => setModalState({...modalState, print: false})} htmlContent={printHtml} />
        </div>
    );
}